/**
 * OrdersDisplaySheet — the mobile Orders "Display" bottom sheet: pick Group by
 * and Sort by (headless menus), and toggle which row-detail fields show. Channel
 * grouping and the Channel field are marked "Not supported" (no channel data).
 */

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ModalSheet } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrdersGroupBy, OrdersSort, RowProp } from "./orders-mobile-model";
import type { OrdersDisplayPrefs } from "./useOrdersDisplayPrefs";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  prefs: OrdersDisplayPrefs;
  onGroup: (g: OrdersGroupBy) => void;
  onSort: (s: OrdersSort) => void;
  onToggleProp: (p: RowProp) => void;
  onResetProps: () => void;
};

const GROUP_OPTIONS: { id: OrdersGroupBy; labelKey: string; ns?: boolean }[] = [
  { id: "none", labelKey: keys.orders.groupByNone },
  { id: "status", labelKey: keys.orders.groupByStatus },
  { id: "date", labelKey: keys.orders.groupByDate },
  { id: "channel", labelKey: keys.orders.groupByChannel, ns: true },
];

const SORT_OPTIONS: { id: OrdersSort; labelKey: string }[] = [
  { id: "newest", labelKey: keys.orders.sortNewest },
  { id: "oldest", labelKey: keys.orders.sortOldest },
  { id: "valueDesc", labelKey: keys.orders.sortValueDesc },
  { id: "valueAsc", labelKey: keys.orders.sortValueAsc },
];

const ROW_PROPS: { id: RowProp; labelKey: string; ns?: boolean }[] = [
  { id: "customer", labelKey: keys.orders.propCustomer },
  { id: "items", labelKey: keys.orders.propItems },
  { id: "value", labelKey: keys.orders.propValue },
  { id: "time", labelKey: keys.orders.propTime },
  { id: "location", labelKey: keys.orders.propLocation },
  { id: "payment", labelKey: keys.orders.propPayment },
  { id: "channel", labelKey: keys.orders.propChannel, ns: true },
];

export function OrdersDisplaySheet({
  isOpen,
  onClose,
  prefs,
  onGroup,
  onSort,
  onToggleProp,
  onResetProps,
}: Props) {
  const t = useTranslations();
  const groupLabel =
    GROUP_OPTIONS.find((o) => o.id === prefs.group)?.labelKey ?? keys.orders.groupByStatus;
  const sortLabel =
    SORT_OPTIONS.find((o) => o.id === prefs.sort)?.labelKey ?? keys.orders.sortNewest;

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="sm" surface="content">
      <div className="flex flex-col p-4">
        <div className="mb-3 flex items-center">
          <h2 className="text-[16px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.orders.displayTitle)}
          </h2>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label={t(keys.common.close)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--soft)] text-[color:var(--text-secondary)]"
          >
            <XIcon />
          </button>
        </div>

        {/* Group by / Sort by */}
        <div className="overflow-hidden rounded-[15px] border border-[color:var(--card-br)]">
          <SelectRow
            label={t(keys.orders.groupByLabel)}
            value={t(groupLabel)}
            options={GROUP_OPTIONS.map((o) => ({
              id: o.id,
              label: t(o.labelKey),
              active: o.id === prefs.group,
              ns: o.ns,
            }))}
            onPick={(id) => onGroup(id as OrdersGroupBy)}
            border
          />
          <SelectRow
            label={t(keys.orders.sortByLabel)}
            value={t(sortLabel)}
            options={SORT_OPTIONS.map((o) => ({
              id: o.id,
              label: t(o.labelKey),
              active: o.id === prefs.sort,
            }))}
            onPick={(id) => onSort(id as OrdersSort)}
          />
        </div>

        {/* Row details */}
        <div className="mb-2 mt-5 flex items-center">
          <span className="text-[13px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            {t(keys.orders.rowDetailsLabel)}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onResetProps}
            className="text-[12.5px] font-semibold text-[color:var(--text-secondary)]"
          >
            {t(keys.orders.resetLabel)}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {ROW_PROPS.map((p) => {
            const on = prefs.props[p.id];
            return (
              <button
                key={p.id}
                type="button"
                disabled={p.ns}
                onClick={() => onToggleProp(p.id)}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold transition-colors disabled:opacity-55"
                style={{
                  background: on ? "var(--soft2)" : "transparent",
                  border: `1px solid ${on ? "transparent" : "var(--line)"}`,
                  color: on ? "var(--text-primary)" : "var(--text-muted)",
                }}
              >
                {on ? <CheckIcon /> : <PlusIcon />}
                {t(p.labelKey)}
                {p.ns ? <NsMini /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </ModalSheet>
  );
}

function SelectRow({
  label,
  value,
  options,
  onPick,
  border = false,
}: {
  label: string;
  value: string;
  options: { id: string; label: string; active: boolean; ns?: boolean }[];
  onPick: (id: string) => void;
  border?: boolean;
}) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={`flex w-full items-center px-4 py-3.5 text-start ${
          border ? "border-b border-[color:var(--card-br)]" : ""
        }`}
      >
        <span className="text-[14.5px] font-semibold text-[color:var(--text-primary)]">{label}</span>
        <span className="flex-1" />
        <span className="me-2 text-[14.5px] font-semibold text-[color:var(--text-muted)]">{value}</span>
        <ChevronsUpDownIcon />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 w-[246px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
      >
        {options.map((o) => (
          <MenuItem key={o.id}>
            <button
              type="button"
              disabled={o.ns}
              onClick={() => onPick(o.id)}
              className="flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-start text-[14px] font-medium text-[color:var(--text-primary)] transition-colors data-[focus]:bg-[color:var(--soft)] disabled:opacity-55"
            >
              <span className="flex w-4 justify-center">{o.active ? <CheckIcon /> : null}</span>
              <span className="flex-1">{o.label}</span>
              {o.ns ? <NsMini /> : null}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

function NsMini() {
  const t = useTranslations();
  return (
    <span className="rounded-full border border-[color:var(--line)] px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[color:var(--text-muted)]">
      {t(keys.orders.notSupported)}
    </span>
  );
}

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function XIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg {...ICON} width={15} height={15} strokeWidth={2.6} className="text-[color:var(--accent-500)]">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg {...ICON} width={14} height={14} strokeWidth={2.4} className="text-[color:var(--text-muted)]">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function ChevronsUpDownIcon() {
  return (
    <svg {...ICON} width={15} height={15} className="text-[color:var(--text-muted)]">
      <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
    </svg>
  );
}
